/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/display-name */

import React from 'react';
import { TableComponents, TableVirtuoso } from 'react-virtuoso';
import { Paper, Table, TableBody, TableCell, TableContainer, TableRow, TableHead, useTheme, CircularProgress } from '@mui/material';
import useScreenSize from 'common/hooks/useScreenSize';
import OrderColumns from 'common/types/OrderColumns';
import StatusLabel from 'common/components/StatusLabel';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as CheckIcon } from 'common/icons/table-check.svg';



interface ColumnData {
  dataKey: keyof OrderColumns;
  label: string;
  numeric?: boolean;
  width: number;
  isName?: boolean;
}

const columns: ColumnData[] = [
  {
    width: 96,
    label: 'Номер',
    dataKey: 'id',
    isName: true,
  },
  {
    width: 122,
    label: 'Клієнт',
    dataKey: 'name',
  },
  {
    width: 110,
    label: 'Статус',
    dataKey: 'status',
  },
  {
    width: 120,
    label: 'Партнер',
    dataKey: 'isPartner',
  },
  {
    width: 144,
    label: 'Прайс-лист',
    dataKey: 'priceList',
  },
  {
    width: 264,
    label: 'Оформлення / Видача',
    dataKey: 'acceptanceDate',
  },
  {
    width: 160,
    label: 'Виконавець',
    dataKey: 'executor',
  },
  {
    width: 168,
    label: 'Пункт видачі',
    dataKey: 'location',
  },
  {
    width: 112,
    label: 'Ціна',
    dataKey: 'price',
  },
];



/*const rows: UserDataProps[] = Array.from({ length: 100 }, () => {
  const randomSelection = sample[Math.floor(Math.random() * sample.length)];
  return createData(...randomSelection);
});*/

const VirtuosoTableComponents: TableComponents<OrderColumns> = {
  Scroller: React.forwardRef<HTMLDivElement>((props, ref) => (
    <TableContainer sx={{
      '&::-webkit-scrollbar': {
        width: '14px',
        padding: '15px 0',
        transform: 'scale(0.5)',
      },
      '&::-webkit-scrollbar-track': {
        background: 'rgba(0, 0, 0, 0)',
      },
      '&::-webkit-scrollbar-corner': {
        background: 'rgba(0, 0, 0, 0)',
      },
      '&::-webkit-scrollbar-thumb': {
        background: 'rgba(64, 108, 255, 0.10)',
        borderRadius: '20px',
        border: '3px solid rgba(0, 0, 0, 0)',
        backgroundClip: 'padding-box',
      },
    }} component={Paper} {...props} ref={ref} />
  )),
  Table: (props) => (
    <Table {...props} sx={{ borderCollapse: 'separate', tableLayout: 'fixed' }} />
  ),
  TableHead,
  // eslint-disable-next-line react/prop-types
  TableRow: ({ item: _item, ...props }) => <TableRow sx={{
    height: '72px',
    maxHeight: '72px',
    cursor: 'pointer',
    '& td': {
      transition: 'background-color .2s ease-in-out',
    },
    '&:hover td': {
      backgroundColor: 'rgba(86, 120, 255, 0.25)',
    },
  }} {...props} />,
  TableBody: React.forwardRef<HTMLTableSectionElement>((props, ref) => (
    <TableBody {...props} ref={ref} />
  )),
};

function fixedHeaderContent() {
  const theme = useTheme();
  return (
    <TableRow sx={{ backgroundColor: theme.colors.secondaryColor }}>
      {columns.map((column, index) => (
        <TableCell
          key={index}
          variant="head"
          align={column.numeric || false ? 'right' : 'left'}
          style={{
            width: column.width,
            fontSize: '20px',
            fontStyle: 'normal',
            fontWeight: '500',
            padding: '0px',
            height: '36px',
            verticalAlign: 'top',
            lineHeight: '100%',
            letterSpacing: '1px',
            color: theme.colors.black,
          }}
          className='table-header-cell'
          sx={{
            backgroundColor: 'transparent',
            borderBottom: 'none',
            textAlign: 'center',
            '&:not(:last-of-type)': {
              borderRight: `1px solid ${theme.colors.gray}`,
            },
            '&:first-of-type .table-head-cell': {
              width: 'calc(100% - 8px)',
              marginLeft: '8px',
            },
            '&:last-of-type .table-head-cell': {
              width: 'calc(100% - 8px)',
            },
          }}
        >
          <div style={{ borderColor: theme.colors.gray }} className='table-head-cell'>
            {column?.label}
          </div>
        </TableCell>
      ))}
    </TableRow>
  );
}



type Props = {
  searchValue?: string;
  data: OrderColumns[];
  loading?: boolean;
};

export default function CustomTable({ searchValue, data, loading }: Props) {
  const theme = useTheme();
  const navigate = useNavigate();
  const screenSize = useScreenSize();
  // Filter data by full name, by number and email
  const filteredData = data.filter((row) => {
    const id = row.id;
    const name = row.name || '';
    const email = row.clientEmail || '';
    const phone = row.clientPhone || '';
    const search = searchValue?.toLowerCase() || '';
    return (id.toString().toLowerCase().includes(search || '') || name.toLowerCase().includes(search || '') ||
      email.toLowerCase().includes(search || '') || phone.toLowerCase().includes(search || ''));
  });

  function rowContent(_index: number, row: OrderColumns) {
    return (
      <React.Fragment>
        {columns.map((column, index) => {
          let result: number | string | boolean | React.ReactNode = row[column.dataKey];
          console.log(row.location);
          if (column.dataKey === 'acceptanceDate') {
            const acceptanceDate = row?.acceptanceDate.split('T')[0].split('-').reverse().join('/');
            result = acceptanceDate;
          }
          // write price with currency and with float .00
          if ( column.dataKey === 'price' ) {
            result = row.price + '₴';
          }
          if (result === undefined || result === null || result?.toString()?.trim() === '') {
            result = '—';
            if (column.dataKey === 'acceptanceDate' && row.issueDate?.length > 0) {
              const issueDate = row?.issueDate.split('T')[0].split('-').reverse().join('/');
              result = issueDate;
            }
          }
          // Change 2000-01-01T00:00:00 to 01/01/2000
  
          let resultJSX = <span className={column.dataKey}>{result}</span>;
  
          if (column.dataKey === 'acceptanceDate' && row.acceptanceDate && row.issueDate) {
            // from Date type to dd/mm/yyyy
            const acceptanceDate = row?.acceptanceDate.split('T')[0].split('-').join('.') + ' ' + row?.acceptanceDate.split('T')[1].split('.')[0].split(':')[0] + ':' + row?.acceptanceDate.split('T')[1].split('.')[0].split(':')[1];
            const issueDate = row?.issueDate.split('T')[0].split('-').join('.') + ' ' + row?.issueDate.split('T')[1].split('.')[0].split(':')[0] + ':' + row?.issueDate.split('T')[1].split('.')[0].split(':')[1];
            const resultDate = `${acceptanceDate !== '0001.01.01 00:00' ? acceptanceDate : '—'}` + '\n' + `${issueDate !== '0001.01.01 00:00' ? issueDate : '—'}`;
            resultJSX = (
              <span className={column.dataKey}>{resultDate}</span>
            );
          }

          if (column.dataKey === 'isPartner') {
            resultJSX = row.isPartner ? <CheckIcon className='svg-black' /> : <></>;
          }
  
          if (column.dataKey === 'status') {
            const statusText = row.status;
            const statusColor = row?.statusColor || theme.colors.gray;
            resultJSX = <StatusLabel status={statusText || ''} color={statusColor} />;
          }
  
          return (
            <TableCell
              key={index}
              align={column.numeric || false ? 'right' : 'left'}
              className='table-cell'
              onClick={() => {navigate(`/services/orders/${row.id.toString()}`);}}
              sx={{
                color: theme.colors.black,
                borderBottom: 'none',
                verticalAlign: 'top',
                padding: '0',
                '&:not(:first-of-type)': {
                  textAlign: 'center',
                },
                '&:not(:last-of-type)': {
                  borderRight: `1px solid ${theme.colors.gray}`,
                },
                '&:first-of-type': {
                  paddingLeft: '8px',
                  borderTopLeftRadius: '8px',
                  borderBottomLeftRadius: '8px',
                },
                '&:last-of-type': {
                  paddingRight: '8px',
                  borderTopRightRadius: '8px',
                  borderBottomRightRadius: '8px',
                },
                '&:first-of-type .table-cell-border': {
                  justifyContent: 'center',
                },
              }}
            >
              <div style={{ borderColor: theme.colors.gray }} className='table-cell-border'>
                {resultJSX}
              </div>
            </TableCell>
          );
        })}
      </React.Fragment>
    );
  }

  return (
    <Paper sx={{
      backgroundColor: 'transparent',
      boxShadow: 'unset',
      borderRadius: 16 + 'px',
      border: `1px solid ${theme.colors.gray}`,
      overflow: 'hidden',
      padding: '16px 8px',
      color: theme.colors.black,
      position: 'relative',

    }} style={{ height: screenSize.height - 296 + 28 - 16, width: '100%' }}>
      {!loading ? <TableVirtuoso
        data={filteredData}
        style={{ boxShadow: 'unset', backgroundColor: 'transparent', color: theme.colors.black }}
        components={VirtuosoTableComponents}
        fixedHeaderContent={fixedHeaderContent}
        itemContent={rowContent}
        onClick={(e)=> {console.log(e);}}
      /> : <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      }}><CircularProgress sx={{

      }} /></div>}
    </Paper>
  );
}